import type { UserRole } from '@/types/auth'
import {
  TREATING_CLINICIAN_ROLES,
  CONSULTATION_COMPLETING_ROLES,
  STAFF_APP_ROLES,
} from './roleSets'

export type Capability =
  | 'nav.dashboard'
  | 'nav.queue'
  | 'nav.patients'
  | 'nav.appointments'
  | 'nav.consultation'
  | 'nav.refraction'
  | 'nav.dispensary'
  | 'nav.surgery'
  | 'nav.finance'
  | 'nav.invoices'
  | 'nav.reports'
  | 'nav.staff'
  | 'nav.products'
  | 'nav.jobs'
  | 'nav.info'
  | 'nav.chat'
  | 'nav.change_requests'
  | 'nav.backups'
  | 'nav.users'
  | 'nav.notifications'
  | 'nav.my_appointments'
  | 'nav.my_visits'
  | 'nav.my_prescriptions'
  | 'nav.my_invoices'
  | 'queue.start_consultation'
  | 'queue.record_visual_acuity'
  | 'queue.mark_refraction'
  | 'consultation.save_progress'
  | 'consultation.complete'
  | 'consultation.save_full'
  | 'consultation.save_visual_acuity'
  | 'consultation.prescribe_restricted'
  | 'consultation.draft_cache'
  | 'patients.delete'
  | 'patients.request_deletion'
  | 'patients.approve_deletion'
  | 'patients.invite_portal'
  | 'patients.create_with_portal'
  | 'patients.attachments.purge'
  | 'patients.manage_files'
  | 'dispensary.process_sale'
  | 'dispensary.edit_sale'
  | 'dispensary.delete_sale'
  | 'dispensary.receive_stock'
  | 'dispensary.transfer_stock'
  | 'dispensary.remove_stock_direct'
  | 'dispensary.request_stock_removal'
  | 'dispensary.approve_stock_removal'
  | 'dispensary.delete_drug'
  | 'surgery.checklist_edit'
  | 'staff.manage'
  | 'staff.invite'
  | 'staff.assign_management'
  | 'users.manage'
  | 'users.invite'
  | 'users.link_staff'
  | 'config.manage'
  | 'backups.export'
  | 'backups.restore'
  | 'notifications.send'
  | 'notifications.prescription_ready'
  | 'notifications.appointment_reminder'
  | 'change_requests.create'
  | 'change_requests.view_errors'
  | 'change_requests.admin'
  | 'platform.test_mode'
  | 'platform.preview_role'
  | 'platform.use_staff_app'
  | 'platform.use_patient_portal'

const OPTOMETRIST_ROLES: readonly UserRole[] = ['Optometrist']

/** capability → roles that grant it (canonical source) */
export const CAPABILITY_GRANTS: Record<Capability, readonly UserRole[]> = {
  'nav.dashboard': [...STAFF_APP_ROLES, 'Patient'],
  'nav.queue': ['SystemAdmin', ...TREATING_CLINICIAN_ROLES, 'Nurse', 'Dispensary'],
  'nav.patients': [
    'SystemAdmin',
    'Management',
    ...TREATING_CLINICIAN_ROLES,
    'Nurse',
    'Dispensary',
  ],
  'nav.appointments': [
    'SystemAdmin',
    'Management',
    ...TREATING_CLINICIAN_ROLES,
    'Nurse',
  ],
  'nav.consultation': ['SystemAdmin', ...TREATING_CLINICIAN_ROLES, 'Nurse'],
  'nav.refraction': ['SystemAdmin', 'Management', ...TREATING_CLINICIAN_ROLES],
  'nav.dispensary': ['SystemAdmin', 'Management', 'Dispensary'],
  'nav.surgery': ['SystemAdmin', 'Management', ...CONSULTATION_COMPLETING_ROLES],
  'nav.finance': ['SystemAdmin', 'Management', 'Accountant'],
  'nav.invoices': ['SystemAdmin', 'Management', 'Accountant'],
  'nav.reports': ['SystemAdmin', 'Management', 'Accountant'],
  'nav.staff': ['SystemAdmin', 'Management'],
  'nav.products': ['SystemAdmin', 'Management'],
  'nav.jobs': ['SystemAdmin', 'Management'],
  'nav.info': [...STAFF_APP_ROLES, 'Patient'],
  'nav.chat': STAFF_APP_ROLES,
  'nav.change_requests': STAFF_APP_ROLES,
  'nav.backups': ['SystemAdmin', 'Management'],
  'nav.users': ['SystemAdmin'],
  'nav.notifications': [...STAFF_APP_ROLES, 'Patient'],
  'nav.my_appointments': ['Patient'],
  'nav.my_visits': ['Patient'],
  'nav.my_prescriptions': ['Patient'],
  'nav.my_invoices': ['Patient'],
  'queue.start_consultation': ['SystemAdmin', ...TREATING_CLINICIAN_ROLES],
  'queue.record_visual_acuity': [
    'SystemAdmin',
    'Management',
    ...TREATING_CLINICIAN_ROLES,
    'Nurse',
  ],
  // Same audience as Assign / Start on the queue (clinicians + nurses who manage visits).
  'queue.mark_refraction': [
    'SystemAdmin',
    'Management',
    ...TREATING_CLINICIAN_ROLES,
    'Nurse',
  ],
  'consultation.save_progress': ['SystemAdmin', ...CONSULTATION_COMPLETING_ROLES],
  'consultation.complete': ['SystemAdmin', ...CONSULTATION_COMPLETING_ROLES],
  'consultation.save_full': [
    'SystemAdmin',
    'Management',
    ...CONSULTATION_COMPLETING_ROLES,
    ...OPTOMETRIST_ROLES,
  ],
  'consultation.save_visual_acuity': [
    'SystemAdmin',
    'Management',
    ...TREATING_CLINICIAN_ROLES,
    'Nurse',
  ],
  'consultation.prescribe_restricted': [
    'SystemAdmin',
    'Management',
    ...CONSULTATION_COMPLETING_ROLES,
  ],
  // Draft-writing roles: clinicians who can author consultation content. Excludes
  // non-writing staff (Nurse, Dispensary, Accountant) — they can read but not write drafts.
  'consultation.draft_cache': [
    'SystemAdmin',
    'Management',
    ...CONSULTATION_COMPLETING_ROLES,
    ...OPTOMETRIST_ROLES,
  ],
  'patients.delete': ['SystemAdmin', 'Management'],
  'patients.request_deletion': [
    'SystemAdmin',
    'Management',
    ...CONSULTATION_COMPLETING_ROLES,
  ],
  'patients.approve_deletion': ['SystemAdmin', 'Management'],
  'patients.invite_portal': ['SystemAdmin'],
  'patients.create_with_portal': ['SystemAdmin'],
  'patients.attachments.purge': ['SystemAdmin'],
  'patients.manage_files': STAFF_APP_ROLES,
  'dispensary.process_sale': STAFF_APP_ROLES,
  'dispensary.edit_sale': ['SystemAdmin', 'Management', 'Dispensary'],
  'dispensary.delete_sale': ['SystemAdmin', 'Management', 'Dispensary'],
  'dispensary.receive_stock': ['SystemAdmin', 'Management', 'Dispensary'],
  'dispensary.transfer_stock': ['SystemAdmin', 'Management', 'Dispensary'],
  'dispensary.remove_stock_direct': ['SystemAdmin', 'Management'],
  'dispensary.request_stock_removal': STAFF_APP_ROLES,
  'dispensary.approve_stock_removal': ['SystemAdmin', 'Management'],
  'dispensary.delete_drug': ['SystemAdmin', 'Management'],
  'surgery.checklist_edit': [
    'SystemAdmin',
    'Management',
    ...CONSULTATION_COMPLETING_ROLES,
    'Nurse',
  ],
  'staff.manage': ['SystemAdmin', 'Management'],
  'staff.invite': ['SystemAdmin', 'Management'],
  'staff.assign_management': ['SystemAdmin'],
  'users.manage': ['SystemAdmin'],
  'users.invite': ['SystemAdmin'],
  'users.link_staff': ['SystemAdmin'],
  'config.manage': ['SystemAdmin', 'Management'],
  'backups.export': ['SystemAdmin', 'Management'],
  'backups.restore': ['SystemAdmin'],
  'notifications.send': ['SystemAdmin', 'Management'],
  'notifications.prescription_ready': [
    'SystemAdmin',
    'Management',
    ...TREATING_CLINICIAN_ROLES,
    'Nurse',
    'Dispensary',
  ],
  'notifications.appointment_reminder': [
    'SystemAdmin',
    'Management',
    ...TREATING_CLINICIAN_ROLES,
    'Nurse',
    'Dispensary',
  ],
  'change_requests.create': STAFF_APP_ROLES,
  'change_requests.view_errors': ['SystemAdmin', 'Management'],
  'change_requests.admin': ['SystemAdmin', 'Management'],
  'platform.test_mode': ['SystemAdmin'],
  'platform.preview_role': ['SystemAdmin'],
  'platform.use_staff_app': STAFF_APP_ROLES,
  'platform.use_patient_portal': ['Patient'],
}

export const ALL_CAPABILITIES = Object.keys(CAPABILITY_GRANTS) as Capability[]
